import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { userQuery } from '@amfam/shared/user';
import { fromRouterActions } from '@amfam/shared/utility/navigation';

import * as fromRoot from '../../../core/store';
import * as kydSmartphoneEnrollSummaryActions from '../../../core/store/programs/kyd-smartphone/kyd-smartphone-enrollment-summary.actions';
import { EnrollKydPolicyRequest } from '../models/kyd-partial-household.model';
import { KydPartialHouseholdService } from '../services/kyd-partial-household.service';
import * as KydPartialHouseholdActions from './kyd-partial-household.actions';

@Injectable()
export class KydPartialHouseholdEffects {
  enrollPolicy$ = createEffect(() =>
    this.action$.pipe(
      ofType(KydPartialHouseholdActions.KydPartialHouseholdActionTypes.EnrollPolicy),
      map((action: KydPartialHouseholdActions.EnrollPolicy) => action.payload),
      withLatestFrom(
        this.store.select(userQuery.selectPartyDetails),
        this.store.select(userQuery.selectCustomerId)
      ),
      switchMap(([payload, party, customerId]) => {
        const request: EnrollKydPolicyRequest = this.kydPhhService.buildEnrollPolicyRequest(
          payload,
          party,
          customerId
        );
        return this.kydPhhService.enrollPolicy(request).pipe(
          mergeMap(res => {
            const actionsArray: Array<any> = [];
            actionsArray.push(new kydSmartphoneEnrollSummaryActions.LoadAction());
            // Choose success Action based on whether all operators or single operator enrolled. This determines the
            // toast message triggered for this transaction.
            if (payload.enrollAll) {
              actionsArray.push(new KydPartialHouseholdActions.EnrollPolicyAllOperatorsSuccess());
            } else {
              actionsArray.push(
                new KydPartialHouseholdActions.EnrollPolicySingleOperatorSuccess(
                  payload.operatorName
                )
              );
            }
            return actionsArray;
          }),
          catchError(error => of(new KydPartialHouseholdActions.EnrollPolicyFail(error)))
        );
      })
    )
  );

  unenrollPolicy$ = createEffect(() =>
    this.action$.pipe(
      ofType(KydPartialHouseholdActions.KydPartialHouseholdActionTypes.UnenrollPolicy),
      map((action: KydPartialHouseholdActions.UnenrollPolicy) => action),
      withLatestFrom(this.store.select(fromRoot.getKydSmartphonePolicyEnrollmentId)),
      switchMap(([payload, enrollmentId]) => {
        return this.kydPhhService.unenrollPolicy(enrollmentId).pipe(
          mergeMap(res => {
            const responseActions: Action[] = [
              new KydPartialHouseholdActions.UnenrollPolicySuccess(),
              new kydSmartphoneEnrollSummaryActions.LoadAction()
            ];
            if (payload.payload.eligibleForUtah)
              responseActions.push(
                fromRouterActions.Go({
                  path: ['/policies/knowyourdrivesmartphone/confirmation']
                })
              );
            return responseActions;
          }),
          catchError(error => of(new KydPartialHouseholdActions.UnenrollPolicyFail(error)))
        );
      })
    )
  );

  enrollOperator$ = createEffect(() =>
    this.action$.pipe(
      ofType(KydPartialHouseholdActions.KydPartialHouseholdActionTypes.EnrollOperator),
      map((action: KydPartialHouseholdActions.EnrollOperator) => action.payload),
      switchMap(payload => {
        return this.kydPhhService.enrollOperator(payload).pipe(
          mergeMap(res => {
            return [
              new KydPartialHouseholdActions.EnrollOperatorSuccess(payload.operatorName),
              new kydSmartphoneEnrollSummaryActions.LoadAction()
            ];
          }),
          catchError(error => of(new KydPartialHouseholdActions.EnrollOperatorFail(error)))
        );
      })
    )
  );

  unenrollOperator$ = createEffect(() =>
    this.action$.pipe(
      ofType(KydPartialHouseholdActions.KydPartialHouseholdActionTypes.UnenrollOperator),
      map((action: KydPartialHouseholdActions.UnenrollOperator) => action.payload),
      switchMap(payload => {
        return this.kydPhhService.unenrollOperator(payload).pipe(
          mergeMap(res => {
            return [
              new KydPartialHouseholdActions.UnenrollOperatorSuccess(payload.operatorName),
              new kydSmartphoneEnrollSummaryActions.LoadAction()
            ];
          }),
          catchError(error => of(new KydPartialHouseholdActions.UnenrollOperatorFail(error)))
        );
      })
    )
  );

  updatePhone$ = createEffect(() =>
    this.action$.pipe(
      ofType(KydPartialHouseholdActions.KydPartialHouseholdActionTypes.UpdateOperatorPhoneNumber),
      map((action: KydPartialHouseholdActions.UpdateOperatorPhoneNumber) => action.payload),
      switchMap(payload => {
        return this.kydPhhService.updatePhone(payload).pipe(
          mergeMap(res => {
            return [
              new KydPartialHouseholdActions.UpdateOperatorPhoneNumberSuccess(),
              new kydSmartphoneEnrollSummaryActions.LoadAction()
            ];
          }),
          catchError(error =>
            of(new KydPartialHouseholdActions.UpdateOperatorPhoneNumberFail(error))
          )
        );
      })
    )
  );

  constructor(
    private action$: Actions,
    private kydPhhService: KydPartialHouseholdService,
    private store: Store<any>
  ) {}
}
